import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import TopBar from '../components/layout/TopBar';
import { Icon } from '@iconify/react';
import { StatCard, SectionHeader, Badge, DataTable, GlassCard, Button, Divider } from '../components/ui';
import { api } from '../lib/api';

// ─── Types ────────────────────────────────────────────────
interface DashboardData {
  kpi: { totalRevenue: number; totalCollected: number; totalCommission: number; activeClients: number; totalClients: number; activeDeals: number; totalSales: number; overdueCount: number };
  pipeline: { stage: string; count: number }[];
  recentSales: { date: string; client: string; lot: string; amount: number; stage: string; type: string }[];
  overdueAlerts: { client: string; lot: string; amount: number; days: number }[];
  clientFunnel: { leads: number; prospects: number; active: number; inactive: number };
  inventory: { total: number; available: number; reserved: number; sold: number };
}

const salesColumns = [
  { key: 'date', header: 'Date' },
  { key: 'client', header: 'Client' },
  { key: 'lot', header: 'Lot' },
  { key: 'amount', header: 'Amount' },
  {
    key: 'stage', header: 'Stage',
    render: (row: any) => {
      const v = row.stage === 'COMPLETED' ? 'available' : row.stage === 'CANCELLED' ? 'sold' : row.stage === 'INQUIRY' ? 'default' : 'reserved';
      return <Badge variant={v as any}>{row.stage.replace('_', ' ')}</Badge>;
    },
  },
  {
    key: 'type', header: 'Type',
    render: (row: any) => <Badge variant={row.type === 'CASH' ? 'available' : 'info' as any}>{row.type}</Badge>,
  },
];

const fmt = (n: number) => `₱${n.toLocaleString()}`;

const Dashboard: React.FC = () => {
  const navigate = useNavigate();
  const [data, setData] = useState<DashboardData | null>(null);

  const fetchStats = useCallback(async () => {
    try {
      const stats = await api.dashboard.stats();
      setData(stats);
    } catch {
      // silently fail, dashboard will show zeros
    }
  }, []);

  useEffect(() => { fetchStats(); }, [fetchStats]);

  const kpi = data?.kpi || { totalRevenue: 0, totalCollected: 0, totalCommission: 0, activeClients: 0, totalClients: 0, activeDeals: 0, totalSales: 0, overdueCount: 0 };
  const pipeline = data?.pipeline || [];
  const recentSales = (data?.recentSales || []).map(s => ({ ...s, amount: fmt(s.amount), date: new Date(s.date).toLocaleDateString('en-PH', { year: 'numeric', month: 'short', day: 'numeric' }) }));
  const overdueAlerts = data?.overdueAlerts || [];
  const funnel = data?.clientFunnel || { leads: 0, prospects: 0, active: 0, inactive: 0 };
  const inventory = data?.inventory || { total: 0, available: 0, reserved: 0, sold: 0 };

  return (
    <>
      <TopBar title="Dashboard" />
      <div style={{ padding: '28px', display: 'flex', flexDirection: 'column', gap: '24px' }}>

        {/* KPI Row */}
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(5, 1fr)', gap: '16px' }}>
          <StatCard label="Total Revenue" value={fmt(kpi.totalRevenue)} icon="solar:wallet-money-bold-duotone"
            trend={`${kpi.totalSales} sales total`} trendUp={true} />
          <StatCard label="Active Clients" value={String(kpi.activeClients)} icon="solar:user-bold-duotone"
            iconBg="rgba(34,197,94,0.12)" iconColor="#22C55E"
            trend={`${kpi.totalClients} total`} trendUp={true} />
          <StatCard label="Active Deals" value={String(kpi.activeDeals)} icon="solar:sale-bold-duotone"
            iconBg="rgba(59,130,246,0.12)" iconColor="#3B82F6"
            trend={`${kpi.totalSales} total sales`} trendUp={true} />
          <StatCard label="Commission" value={fmt(kpi.totalCommission)} icon="solar:hand-money-bold-duotone"
            iconBg="rgba(245,158,11,0.12)" iconColor="#F59E0B"
            trend={`${fmt(kpi.totalCollected)} collected`} trendUp={true} />
          <StatCard label="Overdue" value={String(kpi.overdueCount)} icon="solar:danger-triangle-bold-duotone"
            iconBg={kpi.overdueCount > 0 ? 'rgba(239,68,68,0.12)' : 'rgba(34,197,94,0.12)'}
            iconColor={kpi.overdueCount > 0 ? '#EF4444' : '#22C55E'}
            trend={kpi.overdueCount > 0 ? `${fmt(overdueAlerts.reduce((s, a) => s + a.amount, 0))} total` : 'All clear'}
            trendUp={kpi.overdueCount === 0} />
        </div>

        {/* Pipeline + Overdue Alerts */}
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '20px' }}>
          {/* Pipeline Summary */}
          <GlassCard>
            <SectionHeader title="Pipeline Summary" icon="solar:widget-bold-duotone"
              actions={<Button variant="ghost" size="sm" onClick={() => navigate('/sales')}>View All</Button>} />
            <div style={{ display: 'flex', flexDirection: 'column', gap: '10px', marginTop: '8px' }}>
              {(pipeline.length > 0 ? pipeline : [
                { stage: 'INQUIRY', count: 0 }, { stage: 'RESERVATION', count: 0 },
                { stage: 'CONTRACT_SIGNING', count: 0 }, { stage: 'PAYMENT', count: 0 }, { stage: 'COMPLETED', count: 0 },
              ]).map(p => {
                const colors: Record<string, string> = { INQUIRY: '#94A3B8', RESERVATION: '#F59E0B', CONTRACT_SIGNING: '#3B82F6', PAYMENT: '#0D9488', COMPLETED: '#22C55E' };
                const labels: Record<string, string> = { INQUIRY: 'Inquiry', RESERVATION: 'Reservation', CONTRACT_SIGNING: 'Contract', PAYMENT: 'Payment', COMPLETED: 'Completed' };
                return { stage: labels[p.stage] || p.stage, count: p.count, color: colors[p.stage] || '#94A3B8' };
              }).map((s, i) => (
                <div key={i} style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                  <div style={{ width: '80px', color: 'var(--text-muted)', fontSize: '12px', fontWeight: 600 }}>{s.stage}</div>
                  <div style={{ flex: 1, height: '8px', borderRadius: 'var(--radius-full)', background: 'rgba(148,163,184,0.08)' }}>
                    <div style={{
                      height: '100%', borderRadius: 'var(--radius-full)', background: s.color,
                      width: `${Math.min(100, (s.count / 14) * 100)}%`, transition: 'width 0.5s ease',
                    }} />
                  </div>
                  <span style={{ width: '28px', textAlign: 'right', color: 'var(--text-primary)', fontSize: '13px', fontWeight: 600 }}>{s.count}</span>
                </div>
              ))}
            </div>
          </GlassCard>

          {/* Overdue Alerts */}
          <GlassCard style={overdueAlerts.length > 0 ? { borderColor: 'rgba(239,68,68,0.15)' } : {}}>
            <SectionHeader title="Overdue Alerts" icon="solar:danger-triangle-bold-duotone"
              actions={<Button variant="ghost" size="sm" onClick={() => navigate('/sales')}>Manage</Button>} />
            {overdueAlerts.length > 0 ? (
              <div style={{ display: 'flex', flexDirection: 'column', gap: '10px', marginTop: '8px' }}>
                {overdueAlerts.map((a, i) => (
                  <div key={i} style={{
                    display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                    padding: '10px 12px', borderRadius: 'var(--radius-md)',
                    background: 'rgba(239,68,68,0.06)', borderLeft: '3px solid var(--danger)',
                  }}>
                    <div>
                      <div style={{ color: 'var(--text-primary)', fontWeight: 600, fontSize: '13px' }}>{a.client}</div>
                      <div style={{ color: 'var(--text-muted)', fontSize: '11px' }}>Lot {a.lot} · {a.days} days overdue</div>
                    </div>
                    <span style={{ color: 'var(--danger)', fontWeight: 700, fontSize: '14px' }}>{fmt(a.amount)}</span>
                  </div>
                ))}
              </div>
            ) : (
              <div style={{ padding: '20px 0', textAlign: 'center', color: 'var(--success)', fontSize: '14px' }}>
                <Icon icon="solar:check-circle-bold-duotone" width={24} /><br />All payments up to date!
              </div>
            )}
          </GlassCard>
        </div>

        {/* Recent Sales Table */}
        <div>
          <SectionHeader title="Recent Sales" icon="solar:sale-bold-duotone"
            actions={<Button variant="ghost" size="sm" onClick={() => navigate('/sales')}>View Pipeline</Button>} />
          <DataTable columns={salesColumns} data={recentSales} onRowClick={() => navigate('/sales')} />
        </div>

        {/* Bottom Row: Top Agents + Activity Feed + Inventory */}
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '20px' }}>
          {/* Top Agents */}
          <GlassCard>
            <SectionHeader title="Top Agents" icon="solar:users-group-rounded-bold-duotone" />
            <div style={{ display: 'flex', flexDirection: 'column', gap: '10px', marginTop: '4px' }}>
              {kpi.totalSales === 0 ? (
                <div style={{ padding: '20px', textAlign: 'center', color: 'var(--text-muted)', fontSize: '13px' }}>No agent data yet</div>
              ) : (
                <div style={{ padding: '20px', textAlign: 'center', color: 'var(--text-muted)', fontSize: '13px' }}>Agent rankings will populate as sales data grows</div>
              )}
            </div>
          </GlassCard>

          {/* Activity Feed */}
          <GlassCard>
            <SectionHeader title="Recent Activity" icon="solar:clock-circle-bold-duotone" />
            <div style={{ display: 'flex', flexDirection: 'column', gap: '8px', marginTop: '4px' }}>
              {recentSales.length > 0 ? recentSales.map((s, i) => (
                <div key={i} onClick={() => navigate('/sales')} style={{
                  display: 'flex', gap: '10px', alignItems: 'flex-start', cursor: 'pointer',
                  padding: '8px 10px', borderRadius: 'var(--radius-sm)', transition: 'background 0.15s',
                }}
                  onMouseEnter={e => (e.currentTarget.style.background = 'rgba(13,148,136,0.06)')}
                  onMouseLeave={e => (e.currentTarget.style.background = 'transparent')}>
                  <div style={{
                    width: '28px', height: '28px', borderRadius: 'var(--radius-sm)', flexShrink: 0,
                    background: 'rgba(13,148,136,0.12)', display: 'flex', alignItems: 'center', justifyContent: 'center',
                  }}>
                    <Icon icon="solar:sale-bold-duotone" width={14} style={{ color: '#0D9488' }} />
                  </div>
                  <div style={{ flex: 1 }}>
                    <div style={{ color: 'var(--text-secondary)', fontSize: '12px', lineHeight: 1.4 }}>{s.client} — Lot {s.lot} ({s.stage.replace('_',' ')})</div>
                    <div style={{ color: 'var(--text-faint)', fontSize: '10px', marginTop: '2px' }}>{s.date}</div>
                  </div>
                </div>
              )) : (
                <div style={{ padding: '20px', textAlign: 'center', color: 'var(--text-muted)', fontSize: '13px' }}>No recent activity</div>
              )}
            </div>
          </GlassCard>

          {/* Inventory Overview */}
          <GlassCard>
            <SectionHeader title="Inventory" icon="solar:map-bold-duotone"
              actions={<Button variant="ghost" size="sm" onClick={() => navigate('/inventory')}>View Map</Button>} />
            <div style={{ display: 'flex', flexDirection: 'column', gap: '14px', marginTop: '8px' }}>
              {[
                { label: 'Available', count: inventory.available, total: inventory.total || 1, color: 'var(--success)' },
                { label: 'Reserved', count: inventory.reserved, total: inventory.total || 1, color: 'var(--warning)' },
                { label: 'Sold', count: inventory.sold, total: inventory.total || 1, color: 'var(--danger)' },
              ].map((item, i) => (
                <div key={i}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '5px' }}>
                    <span style={{ color: 'var(--text-secondary)', fontSize: '12px' }}>{item.label}</span>
                    <span style={{ color: 'var(--text-primary)', fontSize: '12px', fontWeight: 600 }}>{item.count}</span>
                  </div>
                  <div style={{ height: '6px', borderRadius: 'var(--radius-full)', background: 'rgba(148,163,184,0.10)' }}>
                    <div style={{
                      height: '100%', borderRadius: 'var(--radius-full)', background: item.color,
                      width: `${(item.count / item.total) * 100}%`, transition: 'width 0.5s ease',
                    }} />
                  </div>
                </div>
              ))}
              <Divider spacing="4px" />
              <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                <span style={{ color: 'var(--text-muted)', fontSize: '12px' }}>Total Lots</span>
                <span style={{ color: 'var(--text-primary)', fontSize: '12px', fontWeight: 700 }}>{inventory.total}</span>
              </div>
            </div>
          </GlassCard>
        </div>

        {/* Client Funnel */}
        <GlassCard>
          <SectionHeader title="Client Funnel" icon="solar:filter-bold-duotone"
            actions={<Button variant="ghost" size="sm" onClick={() => navigate('/clients')}>View Clients</Button>} />
          <div style={{ display: 'flex', gap: '12px', marginTop: '12px' }}>
            {[
              { label: 'Leads', count: funnel.leads, color: '#94A3B8' },
              { label: 'Prospects', count: funnel.prospects, color: '#3B82F6' },
              { label: 'Active', count: funnel.active, color: '#22C55E' },
              { label: 'Inactive', count: funnel.inactive, color: '#64748B' },
            ].map((s, i) => (
              <div key={i} style={{
                flex: 1, padding: '16px', borderRadius: 'var(--radius-md)',
                background: 'var(--bg-elevated)', textAlign: 'center',
                borderTop: `3px solid ${s.color}`,
              }}>
                <div style={{ fontSize: '22px', fontWeight: 700, color: s.color, fontFamily: 'var(--font-heading)' }}>{s.count}</div>
                <div style={{ fontSize: '12px', color: 'var(--text-muted)', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.04em', marginTop: '4px' }}>{s.label}</div>
              </div>
            ))}
          </div>
        </GlassCard>
      </div>
    </>
  );
};

export default Dashboard;